import { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { UserContext } from './react-frontend/context/UserContext';

// Initialize dataLayer array
window.dataLayer = window.dataLayer || [];

const MEASUREMENT_ID = 'G-XP481BCF8C'; // Replace with your GA4 Measurement ID

export const Analytics = () => {
  const location = useLocation();
  const { isLoggedIn, emailId, firstName, state, isCandidate } = useContext(UserContext);

  // Add tab visibility tracking
  useEffect(() => {
    const handleVisibilityChange = () => {
      const currentTab = localStorage.getItem('currentAnalyticsTab');
      const tabStartTime = localStorage.getItem('tabStartTime');
      
      if (document.hidden && currentTab && tabStartTime) {
        const timeSpent = Date.now() - parseInt(tabStartTime);
        window.gtag('event', 'tab_engagement', {
          tab_name: currentTab,
          time_spent: timeSpent,
          content_type: localStorage.getItem('selectionType') || 'default',
          user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
          engagement_type: 'tab_blur'
        });
      } else if (!document.hidden && currentTab) {
        localStorage.setItem('tabStartTime', Date.now().toString());
        window.gtag('event', 'tab_engagement', {
          tab_name: currentTab,
          content_type: localStorage.getItem('selectionType') || 'default',
          user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
          engagement_type: 'tab_focus'
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Track final engagement on unmount
      const currentTab = localStorage.getItem('currentAnalyticsTab');
      const tabStartTime = localStorage.getItem('tabStartTime');
      if (currentTab && tabStartTime) {
        const timeSpent = Date.now() - parseInt(tabStartTime);
        window.gtag('event', 'tab_engagement', {
          tab_name: currentTab,
          time_spent: timeSpent,
          content_type: localStorage.getItem('selectionType') || 'default',
          user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
          engagement_type: 'session_end'
        });
      }
    };
  }, [isLoggedIn, isCandidate]);

  useEffect(() => {
    console.log('Analytics component mounted');
    
    // Load Google Analytics Script
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
    script.async = true;
    script.onload = () => console.log('Google Analytics script loaded');
    script.onerror = (error) => console.error('Error loading Google Analytics script:', error);
    document.head.appendChild(script);

    // Initialize gtag
    function gtag() {
      console.log('gtag called with:', arguments);
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    
    // Enhanced configuration with custom dimensions
    gtag('config', MEASUREMENT_ID, {
      debug_mode: true,
      send_page_view: false, // Disable automatic page views
      user_properties: {
        user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
        user_state: state || 'unknown',
        login_status: isLoggedIn ? 'logged_in' : 'logged_out'
      },
      custom_map: {
        'dimension1': 'user_type',
        'dimension2': 'content_type',
        'dimension3': 'interaction_type',
        'dimension4': 'project_category'
      }
    });

    // Track virtual pageview for SPA
    const trackPageView = () => {
      const currentTab = localStorage.getItem('currentTab') || 'main';
      const contentType = localStorage.getItem('selectionType') || 'default';
      
      console.log('Tracking virtual pageview:', {
        currentTab,
        contentType,
        path: location.pathname
      });

      gtag('event', 'page_view', {
        page_title: document.title,
        page_path: `${location.pathname}/${currentTab}`,
        page_location: window.location.href,
        content_type: contentType,
        user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
        send_to: MEASUREMENT_ID
      });
    };

    trackPageView();

    // Track tab changes
    const trackTabChange = (tabName) => {
      // Store the previous tab and its start time
      const previousTab = localStorage.getItem('currentAnalyticsTab');
      const tabStartTime = localStorage.getItem('tabStartTime');
      
      if (previousTab && tabStartTime) {
        const timeSpent = Date.now() - parseInt(tabStartTime);
        // Track time spent on previous tab
        gtag('event', 'tab_engagement', {
          tab_name: previousTab,
          time_spent: timeSpent,
          content_type: localStorage.getItem('selectionType') || 'default',
          user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
          engagement_type: 'tab_exit'
        });
      }

      // Set new tab info
      localStorage.setItem('currentAnalyticsTab', tabName);
      localStorage.setItem('tabStartTime', Date.now().toString());

      // Track tab entry
      gtag('event', 'tab_engagement', {
        tab_name: tabName,
        content_type: localStorage.getItem('selectionType') || 'default',
        user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
        engagement_type: 'tab_entry'
      });
    };

    // Set up tab change listener
    const tabButtons = document.querySelectorAll('.left-tab-button, .right-tab-button');
    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const tabName = button.textContent.trim();
        trackTabChange(tabName);
      });
    });

    return () => {
      console.log('Analytics component unmounting');
      // Clean up event listeners
      tabButtons.forEach(button => {
        button.removeEventListener('click', () => {});
      });
      // Remove GA script
      const gaScript = document.querySelector(`script[src*="googletagmanager.com/gtag"]`);
      if (gaScript) {
        document.head.removeChild(gaScript);
      }
    };
  }, [location, isLoggedIn, emailId, state, isCandidate]);

  return null;
};

// Enhanced custom hook for event tracking
export const useAnalytics = () => {
  const { isLoggedIn, isCandidate } = useContext(UserContext);

  const trackEvent = (eventName, params = {}) => {
    console.log('Tracking event:', eventName, params);
    if (window.gtag) {
      const enhancedParams = {
        ...params,
        user_type: isLoggedIn ? (isCandidate ? 'politician' : 'voter') : 'visitor',
        content_type: localStorage.getItem('selectionType') || 'default',
        timestamp: new Date().toISOString(),
        send_to: MEASUREMENT_ID
      };

      window.gtag('event', eventName, enhancedParams);
    } else {
      console.error('gtag not initialized');
    }
  };

  // Predefined tracking functions for common events
  const trackVote = (billId, voteType) => {
    trackEvent('vote', {
      bill_id: billId,
      vote_type: voteType,
      interaction_type: 'vote'
    });
  };

  const trackComment = (contentId, contentType) => {
    trackEvent('comment', {
      content_id: contentId,
      content_type: contentType,
      interaction_type: 'comment'
    });
  };

  const trackProjectView = (projectId, category) => {
    trackEvent('project_view', {
      project_id: projectId,
      project_category: category,
      content_type: 'project'
    });
  };

  const trackDonation = (amount, projectId) => {
    trackEvent('donation', {
      value: amount,
      project_id: projectId,
      interaction_type: 'donation'
    });
  };

  return {
    trackEvent,
    trackVote,
    trackComment,
    trackProjectView,
    trackDonation
  };
};